import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, mergeMap, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ClearEditPostComponent,
	CreateBulletinBoardComment,
	CreateBulletinBoardCommentSuccess,
	DeleteBulletinBoardComment,
	DeleteBulletinBoardCommentSuccess,
	DeleteBulletinPost,
	DeleteBulletinPostSuccess,
	DislikePost,
	DislikePostSuccess,
	EBulletinBoardActions,
	GetBulletinPost,
	GetBulletinPostComments,
	GetBulletinPostCommentsSuccess,
	GetBulletinPosts,
	GetBulletinPostsSuccess,
	GetBulletinPostSuccess,
	GetGroupsForBulletinPosts,
	GetGroupsForBulletinPostsSuccess,
	GetPostLikedByInfo,
	GetPostLikedByInfoSuccess,
	GetPostSeenByInfo,
	GetPostSeenByInfoSuccess,
	GetPunchBulletinBoardPostComments,
	GetPunchBulletinBoardPostInfoLikedBy,
	GetPunchBulletinBoardPostInfoSeenBy,
	GetPunchBulletinBoardPosts,
	LikePost,
	LikePostSuccess,
	PatchBulletinBoardComment,
	PatchBulletinBoardCommentSuccess,
	PatchBulletinPost,
	PatchBulletinPostSuccess,
	PostBulletinPost,
	PostBulletinPostSuccess,
	SetSelectedPostForComments,
} from '@app/core/store/actions/bulletin-board.actions';
import {BulletinBoardPost} from '@app/core/models/bulletinBoardPost.model';
import {Group} from '@app/core/models/group.model';
import {BulletinBoardPostComment} from '@app/core/models/bulletinBoardPostComment.model';
import {ClearFileEntries} from '@app/core/store/actions/file-entry.actions';
import {BulletinBoardService} from '@app/core/services/api/bulletin-board/bulletin-board.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {getCurrentState} from '@app/core/helpers';
import {BulletinBoardPostLike} from '@app/core/models/bulletinBoardPostLike.model';
import {User} from '@app/core/models/user.model';
import {PaginationHolder} from '@app/core/models/pagination.model';

@Injectable()
export class BulletinBoardEffects {

	@Effect()
	public onGetBulletinPosts$ = this.actions$.pipe(
		ofType<GetBulletinPosts>(EBulletinBoardActions.GetBulletinPosts),
		switchMap((action: GetBulletinPosts) => from(this.bulletinBoardService.getBulletinBoardPosts(action.page_size, action.page, action.departmentId)).pipe(
			switchMap((res: any) => [
				new GetBulletinPostsSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetBulletinPost$ = this.actions$.pipe(
		ofType<GetBulletinPost>(EBulletinBoardActions.GetBulletinPost),
		switchMap((action: GetBulletinPost) => from(this.bulletinBoardService.getBulletinBoardPost(action.id)).pipe(
			switchMap((post: BulletinBoardPost) => [
				new GetBulletinPostSuccess(post),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetGroupsForBulletinPosts$ = this.actions$.pipe(
		ofType<GetGroupsForBulletinPosts>(EBulletinBoardActions.GetGroupsForBulletinPosts),
		switchMap((action: GetGroupsForBulletinPosts) => from(this.bulletinBoardService.getBulletinBoardPostGroups({
			'filter[canPostMessageBoardPostsIn]': true,
			include: 'department',
		})).pipe(
			switchMap((groups: Group[]) => [
				new GetGroupsForBulletinPostsSuccess(groups),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPostBulletinPost$ = this.actions$.pipe(
		ofType<PostBulletinPost>(EBulletinBoardActions.PostBulletinPost),
		mergeMap((action: PostBulletinPost) => from(this.bulletinBoardService.createPost(action.post)).pipe(
			mergeMap((post: BulletinBoardPost) =>
				[
					new PostBulletinPostSuccess(post),
					new ClearEditPostComponent(),
					new ClearFileEntries(),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onCreateBulletinComment$ = this.actions$.pipe(
		ofType<CreateBulletinBoardComment>(EBulletinBoardActions.CreateBulletinBoardComment),
		mergeMap((action: CreateBulletinBoardComment) => from(this.bulletinBoardService.createComment(action.boardPost.id, action.comment)).pipe(
			mergeMap((comment: BulletinBoardPostComment) =>
				[
					new CreateBulletinBoardCommentSuccess(comment, action.boardPost),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onPatchBulletinComment$ = this.actions$.pipe(
		ofType<PatchBulletinBoardComment>(EBulletinBoardActions.PatchBulletinBoardComment),
		mergeMap((action: PatchBulletinBoardComment) => from(this.bulletinBoardService.patchComment(action.postId, action.commentId, action.comment)).pipe(
			mergeMap((comment: BulletinBoardPostComment) =>
				[
					new PatchBulletinBoardCommentSuccess(comment, action.postId, action.commentId),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onDeleteBulletinComment$ = this.actions$.pipe(
		ofType<DeleteBulletinBoardComment>(EBulletinBoardActions.DeleteBulletinBoardComment),
		mergeMap((action: DeleteBulletinBoardComment) => from(this.bulletinBoardService.deleteComment(action.postId, action.commentId)).pipe(
			mergeMap((comment: BulletinBoardPostComment) =>
				[
					new DeleteBulletinBoardCommentSuccess(comment, action.postId),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onDeleteBulletinBoardCommentSuccess$ = this.actions$.pipe(
		ofType<DeleteBulletinBoardCommentSuccess>(EBulletinBoardActions.DeleteBulletinBoardCommentSuccess),
		mergeMap((action) => {
			const post = getCurrentState(this.store).bulletinBoard.bulletinPosts.find(p => p.id === action.postId);
			if (post.comments.length === 0) {
				this.store.dispatch(new GetBulletinPostComments(10, 1, action.postId));
			}

			return [];
		}),
	);
	@Effect()
	public onPatchBulletinPost$ = this.actions$.pipe(
		ofType<PatchBulletinPost>(EBulletinBoardActions.PatchBulletinPost),
		mergeMap((action: PatchBulletinPost) => from(this.bulletinBoardService.patchPost(action.id, action.post)).pipe(
			mergeMap((post: BulletinBoardPost) => [
				new PatchBulletinPostSuccess(post),
				new ClearEditPostComponent(),
				new ClearFileEntries(),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetBulletinPostComments$ = this.actions$.pipe(
		ofType<GetBulletinPostComments>(EBulletinBoardActions.GetBulletinPostComments),
		switchMap((action: GetBulletinPostComments) => from(this.bulletinBoardService.getBulletinBoardPostComments(action.postId, action.page_size, action.page)).pipe(
			switchMap((res: any) =>
				[
					new SetSelectedPostForComments(action.postId),
					new GetBulletinPostCommentsSuccess(res.data, action.postId, res.pagination),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onDeleteBulletinPost$ = this.actions$.pipe(
		ofType<DeleteBulletinPost>(EBulletinBoardActions.DeleteBulletinPost),
		mergeMap((action: DeleteBulletinPost) => from(this.bulletinBoardService.deleteBulletinBoardPost(action.postId)).pipe(
			mergeMap((deletedPost: BulletinBoardPost) =>
				[
					new DeleteBulletinPostSuccess(action.postId),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onDeleteBulletinPostSuccess$ = this.actions$.pipe(
		ofType<DeleteBulletinPostSuccess>(EBulletinBoardActions.DeleteBulletinPostSuccess),
		mergeMap(() => {
			if (getCurrentState(this.store).bulletinBoard.bulletinPosts.length === 0 && getCurrentState(this.store).bulletinBoard.pagination.total > 0) {
				return [new GetBulletinPosts(10, 1)];
			} else {
				return [];
			}
		}),
	);
	@Effect()
	public onLikePost$ = this.actions$.pipe(
		ofType<LikePost>(EBulletinBoardActions.LikePost),
		mergeMap((action: LikePost) => from(this.bulletinBoardService.likePost(action.bulletinBoardPost)).pipe(
			mergeMap((like: BulletinBoardPostLike) =>
				[
					new LikePostSuccess(like, action.bulletinBoardPost),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onDislikePost$ = this.actions$.pipe(
		ofType<DislikePost>(EBulletinBoardActions.DislikePost),
		mergeMap((action: DislikePost) => from(this.bulletinBoardService.dislikePost(action.bulletinBoardPost)).pipe(
			mergeMap((like: BulletinBoardPostLike) =>
				[
					new DislikePostSuccess(like, action.bulletinBoardPost),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPostSeenByInfo$ = this.actions$.pipe(
		ofType<GetPostSeenByInfo>(EBulletinBoardActions.GetPostSeenByInfo),
		switchMap((action: GetPostSeenByInfo) => from(this.bulletinBoardService.getPostSeenByInfo(action.bulletinBoardPost)).pipe(
			switchMap((users: User[]) =>
				[
					new GetPostSeenByInfoSuccess(users),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPostLikedByInfo$ = this.actions$.pipe(
		ofType<GetPostLikedByInfo>(EBulletinBoardActions.GetPostLikedByInfo),
		switchMap((action: GetPostLikedByInfo) => from(this.bulletinBoardService.getPostLikedByInfo(action.bulletinBoardPost, action.page, action.pageSize)).pipe(
			switchMap((res: PaginationHolder<BulletinBoardPostLike>) =>
				[
					new GetPostLikedByInfoSuccess(res.data, res.pagination),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchBulletinBoardPosts$ = this.actions$.pipe(
		ofType<GetPunchBulletinBoardPosts>(EBulletinBoardActions.GetPunchBulletinBoardPosts),
		switchMap((action: GetPunchBulletinBoardPosts) => from(this.bulletinBoardService.getPunchBulletinBoardPosts(action.page_size, action.page)).pipe(
			switchMap((res: any) => [
				new GetBulletinPostsSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchBulletinBoardPostComments$ = this.actions$.pipe(
		ofType<GetPunchBulletinBoardPostComments>(EBulletinBoardActions.GetPunchBulletinBoardPostComments),
		switchMap((action: GetPunchBulletinBoardPostComments) => from(this.bulletinBoardService.getPunchBulletinBoardPostComments(action.postId, action.page_size, action.page)).pipe(
			switchMap((res: any) =>
				[
					new SetSelectedPostForComments(action.postId),
					new GetBulletinPostCommentsSuccess(res.data, action.postId, res.pagination),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchPostLikedByInfo$ = this.actions$.pipe(
		ofType<GetPunchBulletinBoardPostInfoLikedBy>(EBulletinBoardActions.GetPunchBulletinBoardPostInfoLikedBy),
		switchMap((action: GetPunchBulletinBoardPostInfoLikedBy) => from(this.bulletinBoardService.getPunchPostLikedByInfo(action.bulletinBoardPost)).pipe(
			switchMap((res: PaginationHolder<BulletinBoardPostLike>) =>
				[
					new GetPostLikedByInfoSuccess(res.data, res.pagination),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchPostSeenByInfo$ = this.actions$.pipe(
		ofType<GetPunchBulletinBoardPostInfoSeenBy>(EBulletinBoardActions.GetPunchBulletinBoardPostInfoSeenBy),
		switchMap((action: GetPunchBulletinBoardPostInfoSeenBy) => from(this.bulletinBoardService.getPunchPostSeenByInfo(action.bulletinBoardPost)).pipe(
			switchMap((users: User[]) =>
				[
					new GetPostSeenByInfoSuccess(users),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private bulletinBoardService: BulletinBoardService,
		private store: Store<IAppState>,
	) {
	}
}
